import type { RestoreState } from '../components/RelatedViewsToolbar';

/** Shared router state for Brief ↔ ROI ↔ Rationale so catalogs aren't re-fetched. */
export interface EaBriefSnapshot {
  id: string;
  title: string;
  category: string;
  totalScore?: number;
  valueBand?: string;
}

export interface EaDrilldownNavState {
  restore?: RestoreState;
  from?: string;
  returnTo?: string;
  briefSnapshot?: EaBriefSnapshot;
  rankedIds?: string[];
  catalogTotal?: number;
  /** When true, Brief should open the print/export preview on mount. */
  openExport?: boolean;
}

export function readDrilldownNav(state: unknown): EaDrilldownNavState {
  return (state && typeof state === 'object' ? state : {}) as EaDrilldownNavState;
}

type DrilldownPatch = Partial<Omit<EaDrilldownNavState, 'briefSnapshot'>> & {
  /** Pass `null` to drop a stale snapshot (e.g. prev/next rank). Omit to keep current. */
  briefSnapshot?: EaBriefSnapshot | null;
};

/**
 * Carry rank list + restore fields forward.
 * DMD-1178: also preserve briefSnapshot unless the caller explicitly clears it with null,
 * so Brief ↔ Rationale ↔ ROI tab switches keep the real use-case title.
 */
export function buildDrilldownNav(
  current: EaDrilldownNavState,
  patch: DrilldownPatch = {},
): EaDrilldownNavState {
  const next: EaDrilldownNavState = {
    restore: patch.restore ?? current.restore,
    from: patch.from ?? current.from,
    returnTo: patch.returnTo ?? current.returnTo,
    rankedIds: patch.rankedIds ?? current.rankedIds,
    catalogTotal: patch.catalogTotal ?? current.catalogTotal,
  };

  if ('openExport' in patch) {
    if (patch.openExport) next.openExport = true;
  } else if (current.openExport) {
    next.openExport = true;
  }

  if ('briefSnapshot' in patch) {
    if (patch.briefSnapshot) next.briefSnapshot = patch.briefSnapshot;
  } else if (current.briefSnapshot) {
    next.briefSnapshot = current.briefSnapshot;
  }

  return next;
}

/**
 * DMD-1186: primary ← back always exits the use-case drilldown (All Opportunities /
 * Execution portfolio). Sibling Brief ↔ Rationale ↔ ROI tabs are separate controls.
 */
export function resolveDrilldownExit(nav: EaDrilldownNavState): {
  label: string;
  path: string;
  fromActivate: boolean;
} {
  const returnTo = typeof nav.returnTo === 'string' ? nav.returnTo.trim() : '';
  const fromActivate = nav.from === 'activate' || returnTo.includes('/ea/activate');
  if (fromActivate) {
    return {
      label: 'Execution portfolio',
      path: returnTo || '/ea/activate',
      fromActivate: true,
    };
  }
  return {
    label: 'Back to All Opportunities',
    path: returnTo || '/ea/opportunities',
    fromActivate: false,
  };
}
